/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useRef, useMemo } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { TableCellProps, TableRow } from '../../atoms';

export const TableRowBasicStyle = styled(TableRow)`
    .a-checkbox label {
        padding-left: 0;
        display: initial;
    }
`;

const TableRowStyle = styled(TableRowBasicStyle)`
    &:hover {
        > td {
            /* this override adjusts the checkbox inside the table */
            --plain__enabled__fill__default: var(
                --plain__enabled__fill__hovered
            );
            background-color: var(--plain__enabled__fill__hovered);
        }
    }
    &.selected {
        > td {
            /* this override adjusts the checkbox inside the table */
            --plain__enabled__fill__default: var(
                --minor-highlight-blue__enabled__fill__hovered
            );
            background-color: var(
                --minor-highlight-blue__enabled__fill__hovered
            );
        }
    }
    &.disabled {
        > td {
            color: var(--major-accent__disabled__front__default);
            cursor: not-allowed;
        }
    }
`;

export interface DataListRowProps {
    isSelected?: boolean;
    isDisabled?: boolean;
    onSelect?: () => void;
    children:
        | Array<React.ReactElement<TableCellProps>>
        | React.ReactElement<TableCellProps>;
}

export const DataListRow: React.FunctionComponent<DataListRowProps> = ({
    isSelected = false,
    isDisabled = false,
    onSelect,
    children,
}) => {
    const trRef = useRef<HTMLTableRowElement>();

    function handleSelect(event: React.KeyboardEvent<HTMLTableRowElement>) {
        if (
            event.target === trRef.current && // only if target is the tr itself
            (event.key === ' ' || event.key === 'Enter') && // space or enter
            !isDisabled
        ) {
            onSelect();
        }
    }

    function preventPageScrollOnSpaceKey(
        event: React.KeyboardEvent<HTMLTableRowElement>,
    ) {
        if (event.key === ' ') {
            event.preventDefault();
        }
    }

    const tableRowClass = cn({
        selected: isSelected,
        disabled: isDisabled,
    });

    const style = useMemo(() => {
        if (isDisabled) {
            return undefined;
        } else {
            return { cursor: onSelect ? 'pointer' : 'inherit' };
        }
    }, [isDisabled, onSelect]);

    return (
        <TableRowStyle
            ref={trRef}
            className={tableRowClass}
            tabIndex={0}
            onKeyUp={handleSelect}
            onKeyDown={preventPageScrollOnSpaceKey}
            style={style}
        >
            {children}
        </TableRowStyle>
    );
};
